// Monta un bloque de una página de producto dentro de un shadow root.
//
// Los bloques de las páginas de producto traen el marcado y el CSS del sitio de marketing
// —y en planificaciones, el del renderizador real del producto—, con nombres
// como `.slide`, `.container`, `.header`, `.msg`, `.section` o `.btn` que
// chocan de frente con los del SPA. Soltarlos en la página repinta la
// aplicación entera; un iframe terminaba bloqueado. El shadow root aísla en
// las dos direcciones sin marco.
//
// Tres cosas que aprendimos a la mala y que por eso viven acá, en un solo
// sitio:
//
// · `:root` no engancha dentro de un shadow root —solo casa con el elemento
//   raíz del documento—, así que las variables de site.css llegan aparte
//   (`vars`) y se declaran sobre `:host`, desde donde heredan.
// · Las hojas llegan como TEXTO, no como URL: en el servidor de desarrollo de
//   webpack /static/ es suyo y devolvía el index.html del SPA, sin error.
// · Los `ajustes` van DESPUÉS de las hojas: pesan lo mismo que sus reglas y
//   gana la última. Y ojo con las media queries de las hojas: dentro de un
//   shadow root siguen midiendo la VENTANA, no el hueco del chat, así que los
//   diseños "de pantalla angosta" hay que forzarlos acá.
//
// El payload lo sirve backend/marketing/views._bloque_en_sombra.

const estilo = (texto) => {
  const el = document.createElement("style");
  el.textContent = texto;
  return el;
};

// Devuelve `{ raiz, cuerpo }`, o `null` si no se puede montar: sin shadow DOM,
// o sin hoja (saldría el texto pelado, que es peor que no mostrar nada).
export function montarEnSombra(
  host,
  { html, css, vars },
  { ajustes = "", clase = "" } = {}
) {
  if (!host || !host.attachShadow || !css) return null;
  const raiz = host.attachShadow({ mode: "open" });
  const cuerpo = document.createElement("div");
  cuerpo.className = clase;
  cuerpo.innerHTML = html;
  raiz.append(estilo(`:host{${vars}}`), estilo(css), estilo(ajustes), cuerpo);
  return { raiz, cuerpo };
}

// --- lo que comparten los cuerpos de producto montados así -----------------
// Cada una devuelve su limpieza, para el `beforeDestroy` del componente.

// Los enlaces a `#algo` del bloque: dentro del SPA ese hash lo toma el router
// de Vue, así que el salto se hace a mano.
export function saltosInternos(raiz) {
  const anclas = (e) => {
    if (e.defaultPrevented) return;
    const a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    e.preventDefault();
    const destino = raiz.getElementById(a.getAttribute("href").slice(1));
    if (destino) destino.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  raiz.addEventListener("click", anclas);
  return () => raiz.removeEventListener("click", anclas);
}

// Entradas al hacer scroll (port de marketing/_anim.html). site.css apaga
// `[data-anim]` detrás de `.js` hasta que entra en pantalla; sin este
// observador las fichas quedarían invisibles.
export function entradas(raiz) {
  const io = new IntersectionObserver(
    (es) =>
      es.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add("on");
        io.unobserve(e.target);
      }),
    { threshold: 0.25 }
  );
  raiz
    .querySelectorAll("[data-anim],.campana,.pantalla,.cifra")
    .forEach((el) => io.observe(el));
  return () => io.disconnect();
}

// El selector de documentos o de test (#picker, #pmenu, #deck): pasar por un
// ítem adelanta su portada; en pantalla rotan solos cada 1,5 s, pero solo con
// movimiento.
export function selector(raiz, conMovimiento) {
  const menu = raiz.getElementById("pmenu");
  const deck = raiz.getElementById("deck");
  const picker = raiz.getElementById("picker");
  if (!menu || !deck || !picker) return () => {};
  const items = Array.from(menu.querySelectorAll(".picker__it"));
  const pages = Array.from(deck.querySelectorAll(".page"));
  let cur = 0;
  let timer = null;
  let paused = false;
  const set = (k) => {
    if (k === cur) return;
    cur = k;
    items.forEach((b, i) => b.classList.toggle("on", i === k));
    pages.forEach((p, i) => p.classList.toggle("on", i === k));
  };
  const stop = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };
  const start = () => {
    stop();
    timer = setInterval(() => {
      if (!paused) set((cur + 1) % items.length);
    }, 1500);
  };
  const idx = (e, sel) => {
    const el = e.target.closest(sel);
    return el ? +el.getAttribute("data-i") : -1;
  };
  menu.addEventListener("mouseover", (e) => {
    const k = idx(e, ".picker__it");
    if (k >= 0) set(k);
  });
  menu.addEventListener("click", (e) => {
    const k = idx(e, ".picker__it");
    if (k >= 0) {
      set(k);
      if (timer) start();
    }
  });
  menu.addEventListener("focusin", (e) => {
    const k = idx(e, ".picker__it");
    if (k >= 0) set(k);
  });
  deck.addEventListener("mouseover", (e) => {
    const k = idx(e, ".page");
    if (k >= 0) set(k);
  });
  picker.addEventListener("mouseenter", () => {
    paused = true;
  });
  picker.addEventListener("mouseleave", () => {
    paused = false;
  });
  if (!conMovimiento) return () => {};
  const io = new IntersectionObserver(
    (es) => es.forEach((e) => (e.isIntersecting ? start() : stop())),
    { threshold: 0.25 }
  );
  io.observe(picker);
  return () => {
    io.disconnect();
    stop();
  };
}
